import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from './Modal';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { deleteShareholder, fetchShareholders } from '../shareholdersApi';
import {
  normalizeShareStockType,
  normalizeShareholderType,
  shareholderTypeLabel,
  shareStockTypeLabel,
  SHARE_STOCK_TYPE_OPTIONS,
  SHAREHOLDER_TYPE_OPTIONS,
} from '../shareholderFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import { useBranchFilter } from '../branchFilter';
import './BranchesPage.css';
import './BoardPage.css';

const SHAREHOLDER_TYPE_COLORS = {
  'مساهم مؤسس':   '#00334a',
  'مساهم':        '#02c3a6',
  'مساهم مستثمر': '#02c3a6',
};

const SHARE_STOCK_COLORS = {
  'سهم عادي':           '#02c3a6',
  'سهم ممتاز':          '#02c3a6',
  'مخصصة للعاملين':     '#02c3a6',
};

const PAGE_SIZE = 8;

function ShareholdersPage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const sortLocale = (language === 'ar' || language === 'ur') ? 'ar' : language === 'zh' ? 'zh' : language === 'fr' ? 'fr' : 'en';

  const navigate = useNavigate();
  const { selectedBranch: globalBranch } = useBranchFilter();
  const [data, setData]                   = useState([]);
  const [search, setSearch]               = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterType, setFilterType]       = useState('all');
  const [filterShareStock, setFilterShareStock] = useState('all');
  const [currentPage, setPage]            = useState(1);
  const [sortBy, setSortBy]               = useState('ownership-desc');
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const [confirmOpen, setConfirmOpen]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSecretaryContextUser()) {
      setAccessDenied(true);
      setLoadingList(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadError('');
      setLoadingList(true);
      try {
        const rows = await fetchShareholders();
        if (!cancelled) setData(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('shareholders.loadFailed'));
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveBranch = globalBranch !== 'all' ? globalBranch : filterBranch;

  const filtered = useMemo(() => {
    let list = data.filter((s) => {
      const q = search.trim();
      const qLower = q.toLowerCase();
      const shType = normalizeShareholderType(s.type);
      const stockT = normalizeShareStockType(s.shareStockType);
      const typeLabel = shareholderTypeLabel(shType, language).toLowerCase();
      const stockLabel = shareStockTypeLabel(stockT, language).toLowerCase();
      const uName = (s.userName && String(s.userName)) || '';
      const matchSearch = !q
        || s.name.includes(search)
        || s.branch.includes(search)
        || shType.includes(search)
        || stockT.includes(search)
        || typeLabel.includes(qLower)
        || stockLabel.includes(qLower)
        || (uName && (uName.includes(q) || uName.toLowerCase().includes(qLower)));
      const matchBranch = effectiveBranch === 'all' || s.branch === effectiveBranch;
      const matchType    = filterType === 'all' || shType === filterType;
      const matchStock   = filterShareStock === 'all' || stockT === filterShareStock;
      return matchSearch && matchBranch && matchType && matchStock;
    });
    if (sortBy === 'ownership-desc') list = [...list].sort((a, b) => b.ownership - a.ownership);
    if (sortBy === 'ownership-asc')  list = [...list].sort((a, b) => a.ownership - b.ownership);
    if (sortBy === 'name')           list = [...list].sort((a, b) => a.name.localeCompare(b.name, sortLocale));
    return list;
  }, [data, search, effectiveBranch, filterType, filterShareStock, sortBy, language, sortLocale]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const totalOwnership = data.reduce((sum, s) => sum + +s.ownership, 0).toFixed(1);

  const openDelete = (item) => { setDeleteTarget(item); setConfirmOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteShareholder(deleteTarget.id);
      setData((p) => p.map((s) => (s.id === deleteTarget.id ? { ...s, status: 'inactive' } : s)));
      setConfirmOpen(false);
      setDeleteTarget(null);
      setPage(1);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : t('shareholders.deleteFailed'));
      setConfirmOpen(false);
      setDeleteTarget(null);
    }
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>
      </div>
    );
  }

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="money" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t('shareholders.title')}
            </span>
            <span className="list-page-card__sub">{t('shareholders.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button className="btn btn--primary" onClick={() => navigate('/app/shareholders/new')}>
              <Icon name="plus" size={14} color="#fff" /> {t('shareholders.add')}
            </button>
          </div>
        </header>

        {loadError && (
          <div className="list-page-card__banner list-page-card__banner--error">{loadError}</div>
        )}

        {loadingList && (
          <div className="list-page-card__loading">{t('shareholders.loading')}</div>
        )}

      {!loadingList && (
      <>
      <div className="page__stats">
        <div className="page__stat-chip page__stat-chip--total"><Icon name="money" size={13} /> {t('common.total')}: {data.length}</div>
        <div className="page__stat-chip page__stat-chip--active"><Icon name="chart" size={13} /> {t('shareholders.totalOwnership')}: {totalOwnership}%</div>
        <div className="page__stat-chip page__stat-chip--new"><Icon name="branches" size={13} /> {t('shareholders.branches')}: {new Set(data.map((d) => d.branch)).size}</div>
      </div>

      <div className="page__filters">
        <div className="page__search">
          <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
          <input
            className="page__search-input"
            placeholder={t('shareholders.search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          {search && <span style={{ cursor: 'pointer', color: '#90a4ae', fontSize: '0.8rem' }} onClick={() => setSearch('')}>✕</span>}
        </div>
        <select className="page__filter-select" value={filterBranch} onChange={(e) => { setFilterBranch(e.target.value); setPage(1); }}>
          <option value="all">{t('shareholders.allBranches')}</option>
          {Array.from(new Set(data.map((d) => d.branch))).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="page__filter-select" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPage(1); }}>
          <option value="all">{t('shareholders.allTypes')}</option>
          {SHAREHOLDER_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{shareholderTypeLabel(opt.value, language)}</option>)}
        </select>
        <select className="page__filter-select" value={filterShareStock} onChange={(e) => { setFilterShareStock(e.target.value); setPage(1); }}>
          <option value="all">{t('shareholders.allStocks')}</option>
          {SHARE_STOCK_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{shareStockTypeLabel(opt.value, language)}</option>)}
        </select>
        <select className="page__filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="ownership-desc">{t('shareholders.sortOwnershipDesc')}</option>
          <option value="ownership-asc">{t('shareholders.sortOwnershipAsc')}</option>
          <option value="name">{t('shareholders.sortName')}</option>
        </select>
      </div>

      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 42 }}>#</th>
              <th>{t('shareholders.colShareholder')}</th>
              <th>{t('shareholders.colUsername')}</th>
              <th>{t('shareholders.colBranch')}</th>
              <th>{t('shareholders.colType')}</th>
              <th>{t('shareholders.colStockType')}</th>
              <th>{t('shareholders.colOwnership')}</th>
              <th>{t('common.phone')}</th>
              <th>{t('common.email')}</th>
              <th>{t('shareholders.colBankAccount')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={11}>
                <div className="empty-state">
                  <div className="empty-state__icon"><Icon name="money" size={48} color="#cfd8dc" strokeWidth={1.2} /></div>
                  <div className="empty-state__title">{t('shareholders.noMatch')}</div>
                  <div className="empty-state__sub">{t('shareholders.noMatchSub')}</div>
                  <button className="btn btn--primary" onClick={() => navigate('/app/shareholders/new')}><Icon name="plus" size={14} color="#fff" /> {t('shareholders.add')}</button>
                </div>
              </td></tr>
            ) : paginated.map((item, idx) => {
              const shType = normalizeShareholderType(item.type);
              const tc = SHAREHOLDER_TYPE_COLORS[shType] ?? '#02c3a6';
              const stockT = normalizeShareStockType(item.shareStockType);
              const sc = SHARE_STOCK_COLORS[stockT] ?? '#02c3a6';
              return (
              <tr key={item.id}>
                <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                <td>
                  <div className="branch-name-cell">
                    <div className="board-avatar" style={{ background: tc }}>
                      {item.name.charAt(0)}
                    </div>
                    <strong style={{ color: '#00334a', fontSize: '0.88rem' }}>{item.name}</strong>
                  </div>
                </td>
                <td dir="ltr" style={{ textAlign: 'right', fontSize: '0.85rem', color: '#455a64' }}>{item.userName && String(item.userName).trim() ? item.userName : '—'}</td>
                <td>{item.branch}</td>
                <td>
                  <span className="position-badge" style={{ background: `${tc}18`, color: tc, borderColor: `${tc}44` }}>
                    {shareholderTypeLabel(shType, language)}
                  </span>
                </td>
                <td>
                  <span className="position-badge" style={{ background: `${sc}18`, color: sc, borderColor: `${sc}44` }}>
                    {shareStockTypeLabel(stockT, language)}
                  </span>
                </td>
                <td>
                  <div className="ownership-bar">
                    <div className="ownership-bar__track">
                      <div className="ownership-bar__fill" style={{ width: `${Math.min(item.ownership, 100)}%` }} />
                    </div>
                    <span className="ownership-bar__pct">{item.ownership}%</span>
                  </div>
                </td>
                <td dir="ltr" style={{ textAlign: 'right' }}>{item.phone}</td>
                <td dir="ltr" style={{ textAlign: 'right' }}>{item.email}</td>
                <td dir="ltr" style={{ textAlign: 'right' }}>{item.bankAccountNumber || item.iban || '-'}</td>
                <td>
                  <div className="td-actions">
                    <button className="action-btn action-btn--view"   title={t('shareholders.actionView')} onClick={() => navigate(`/app/shareholders/${item.id}`)}><Icon name="eye"   size={14} /></button>
                    <button className="action-btn action-btn--edit"   title={t('shareholders.actionEdit')} onClick={() => navigate(`/app/shareholders/${item.id}/edit`)}><Icon name="edit"  size={14} /></button>
                    <button className="action-btn action-btn--delete" title={t('shareholders.actionDelete')} onClick={() => openDelete(item)}><Icon name="trash" size={14} /></button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>

        {filtered.length > PAGE_SIZE && (
          <div className="pagination">
            <span>{t('common.showing')} {Math.min(filtered.length, (currentPage - 1) * PAGE_SIZE + 1)}–{Math.min(filtered.length, currentPage * PAGE_SIZE)} {t('common.of')} {filtered.length}</span>
            <div className="pagination__pages">
              <button className="pagination__btn" onClick={() => setPage((p) => p - 1)} disabled={currentPage === 1}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="pagination__btn" onClick={() => setPage((p) => p + 1)} disabled={currentPage === totalPages}>›</button>
            </div>
          </div>
        )}
      </div>
      </>
      )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.name}
        title={t('common.deleteConfirmTitle')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
      />
    </div>
  );
}

export default ShareholdersPage;
